// Floating "Need help?" widget. Mounted globally inside AppLayout so it
// appears on every authenticated page. Talks to /api/help/* (server-side
// platform help bot — uses the platform OPENAI_API_KEY, NOT the tenant
// quota). History is kept in component state only — no DB writes.
//
// We export both:
//   * <HelpAssistantWidget /> — the floating bottom-right button (default).
//   * <HelpAssistantPinnedCard /> — embeddable card surface, used on the
//     Chatbots page so the help bot is also discoverable from there.
//   * useHelpAssistant() — exposes openHelp() so any pinned card or link
//     can pop the same dialog.

import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Send, X, MessageCircleQuestion, Lightbulb, Wand2, CheckCircle2, XCircle } from "lucide-react";

type HelpInfo = {
  name: string;
  shortName: string;
  greeting: string;
  tagline: string;
  suggestions: string[];
};

type ProposedAction = { tool: string; summary: string; params: Record<string, unknown> };
type ActionState = "pending" | "running" | "approved" | "rejected" | "error";
type Turn = {
  role: "user" | "assistant";
  content: string;
  action?: ProposedAction;
  actionState?: ActionState;
  resultSummary?: string;
};

type SetupAgentInfo = {
  available: boolean;
  currentPlan: string;
  planRequired: string;
  greeting: string;
  tagline: string;
  suggestions: string[];
};

const HelpContext = createContext<{ openHelp: () => void } | null>(null);

export function useHelpAssistant() {
  const ctx = useContext(HelpContext);
  return { openHelp: ctx?.openHelp ?? (() => {}) };
}

export function HelpAssistantProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <HelpContext.Provider value={{ openHelp: () => setOpen(true) }}>
      {children}
      <HelpAssistantWidget open={open} onOpenChange={setOpen} />
    </HelpContext.Provider>
  );
}

export function HelpAssistantWidget({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: info } = useQuery<HelpInfo>({ queryKey: ["/api/help/info"], enabled: open });
  const { data: setupInfo } = useQuery<SetupAgentInfo>({ queryKey: ["/api/setup-agent/info"], enabled: open });
  const [mode, setMode] = useState<"help" | "setup">("help");
  const [history, setHistory] = useState<Record<"help" | "setup", Turn[]>>({ help: [], setup: [] });
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  // Announce-once live region text (4.1.3). Set ONLY from user-initiated send/decide
  // flows — never from history seeding or mode switches — so replies/errors are
  // announced exactly once, with no restored-history or duplicate announcements.
  const [srAnnouncement, setSrAnnouncement] = useState("");
  const scroller = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // WCAG 2.4.3 Focus Order: when the dialog opens, move keyboard focus to the
  // first focusable element inside the panel so keyboard/screen-reader users
  // don't have to Tab through the entire page to reach the dialog controls.
  useEffect(() => {
    if (!open) return;
    // Wait one animation frame for the panel to be visible and for the
    // "fade-in" animation to have started before moving focus.
    requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const firstFocusable = panel.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    });
  }, [open]);
  const setupAvailable = setupInfo?.available ?? false;
  const turns = history[mode];
  function setTurns(updater: (prev: Turn[]) => Turn[]) {
    setHistory((h) => ({ ...h, [mode]: updater(h[mode]) }));
  }

  // Seed greeting once /info loads — for whichever mode is active.
  useEffect(() => {
    if (mode === "help" && info && history.help.length === 0) {
      setHistory((h) => ({ ...h, help: [{ role: "assistant", content: info.greeting }] }));
    }
    if (mode === "setup" && setupInfo && history.setup.length === 0) {
      setHistory((h) => ({ ...h, setup: [{ role: "assistant", content: setupInfo.greeting }] }));
    }
  }, [mode, info, setupInfo, history.help.length, history.setup.length]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [turns, pending]);

  // Escape closes the help dialog (standard overlay behavior); focus returns
  // to the floating launcher on the next paint so keyboard users aren't lost.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOpenChange(false);
        requestAnimationFrame(() => {
          (document.querySelector('[data-testid="button-open-help-assistant"]') as HTMLElement | null)?.focus();
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const [location] = useLocation();
  const { user } = useAuth();

  async function send(message: string) {
    const trimmed = message.trim();
    if (!trimmed || pending) return;
    const next: Turn[] = [...turns, { role: "user", content: trimmed }];
    setTurns(() => next);
    setInput("");
    setPending(true);
    setSrAnnouncement("Assistant is typing…");
    try {
      if (mode === "setup") {
        const apiHistory = next.slice(-10).map((t) => ({ role: t.role, content: t.content }));
        const res = await apiRequest("POST", "/api/setup-agent/chat", { message: trimmed, history: apiHistory });
        const data = (await res.json()) as { reply: string; action?: ProposedAction | null };
        setTurns(() => [
          ...next,
          { role: "assistant", content: data.reply, action: data.action ?? undefined, actionState: data.action ? "pending" : undefined },
        ]);
        setSrAnnouncement(data.reply);
      } else {
        // RTM Req 47: include current route + user permissions so the assistant
        // can give context-aware answers about the page the user is on.
        const activePermissions: string[] = Array.isArray((user as any)?.permissions)
          ? (user as any).permissions
          : [];
        const res = await apiRequest("POST", "/api/help/chat", {
          message: trimmed,
          history: turns.slice(-10).map((t) => ({ role: t.role, content: t.content })),
          currentRoute: location,
          activePermissions,
        });
        const data = (await res.json()) as { reply: string };
        setTurns(() => [...next, { role: "assistant", content: data.reply }]);
        setSrAnnouncement(data.reply);
      }
    } catch (e: any) {
      const errText = mode === "setup"
        ? "Sorry, I hit an error. Please try again."
        : "Sorry, I couldn't reach the help service. Please try again.";
      setTurns(() => [...next, { role: "assistant", content: errText }]);
      // Essential error — always announced (same policy as the chat surfaces).
      setSrAnnouncement(errText);
    } finally {
      setPending(false);
    }
  }

  async function decide(idx: number, approve: boolean) {
    const turn = turns[idx];
    if (turn.role !== "assistant" || !turn.action) return;
    // Idempotency guard — only act on a pending action. Prevents
    // double-execute on rapid double-click of Approve.
    if (turn.actionState !== "pending") return;
    if (!approve) {
      setTurns((h) => h.map((t, i) => (i === idx ? { ...t, actionState: "rejected" } : t)));
      return;
    }
    setTurns((h) => h.map((t, i) => (i === idx ? { ...t, actionState: "running" } : t)));
    try {
      const res = await apiRequest("POST", "/api/setup-agent/execute", { tool: turn.action.tool, params: turn.action.params });
      const data = (await res.json()) as { ok: boolean; summary: string };
      setTurns((h) => h.map((t, i) => (i === idx ? { ...t, actionState: "approved", resultSummary: data.summary } : t)));
      setSrAnnouncement(data.summary || "Action completed.");
      toast({ title: "Done", description: data.summary });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/me/chatbots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
    } catch (e: any) {
      const msg = e?.message || "Action failed.";
      setTurns((h) => h.map((t, i) => (i === idx ? { ...t, actionState: "error", resultSummary: msg } : t)));
      setSrAnnouncement(msg);
      toast({ title: "Action failed", description: msg, variant: "destructive" });
    }
  }

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          data-testid="button-open-help-assistant"
          aria-label="Need help?"
          // Below sm (i.e. 320px reflow / high zoom) the launcher collapses to an
          // icon-only circle so it covers as little content as possible; the
          // accessible name is preserved via aria-label.
          className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 px-0 w-12 justify-center sm:w-auto sm:justify-start sm:pl-3 sm:pr-4 h-12 rounded-full shadow-lg bg-primary text-primary-foreground hover:shadow-xl transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:shadow-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-white/20" aria-hidden="true">
            <Sparkles className="w-4 h-4 shrink-0" stroke="currentColor" fill="none" aria-hidden="true" />
          </span>
          <span className="hidden sm:inline text-sm font-medium">Need help?</span>
        </button>
      )}

      {/* Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div
            role="button"
            tabIndex={0}
            aria-label="Close help assistant"
            className="absolute inset-0 bg-black/30 pointer-events-auto opacity-0 animate-in fade-in"
            style={{ animationDuration: "150ms", animationFillMode: "forwards" }}
            onClick={() => onOpenChange(false)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === "Escape") && onOpenChange(false)}
            data-testid="overlay-help-assistant"
          />
          <div
            ref={panelRef}
            className="absolute bottom-5 right-5 w-[min(420px,calc(100vw-2.5rem))] h-[min(640px,calc(100vh-2.5rem))] flex flex-col rounded-2xl bg-card border shadow-2xl pointer-events-auto overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Help assistant"
            data-testid="dialog-help-assistant"
          >
            {/* Single polite announce-once live region for this surface (4.1.3).
                Focus is never moved here; content set only on send/decide outcomes. */}
            <div className="sr-only" aria-live="polite" aria-atomic="true" role="status" data-testid="status-help-announcer">
              {srAnnouncement}
            </div>
            <header className="flex items-start gap-3 px-4 py-3 border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-primary text-primary-foreground shrink-0">
                <Sparkles className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold truncate" data-testid="text-help-title">
                    {mode === "setup" ? "Setup Agent" : info?.name ?? "Platform Help"}
                  </h2>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                    {mode === "setup" ? "Enterprise" : "Built-in"}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground line-clamp-2">
                  {mode === "setup"
                    ? (setupInfo?.tagline ?? "AI that configures your platform — with your approval.")
                    : (info?.tagline ?? "Your built-in product expert.")}
                </p>
                {setupAvailable && (
                  <div className="mt-1.5 inline-flex rounded-full bg-muted p-0.5 text-[10px]" data-testid="toggle-help-mode">
                    <button
                      type="button"
                      onClick={() => setMode("help")}
                      className={`px-2 py-0.5 rounded-full transition-colors ${mode === "help" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                      data-testid="button-mode-help"
                    >
                      Help
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode("setup")}
                      className={`px-2 py-0.5 rounded-full transition-colors ${mode === "setup" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                      data-testid="button-mode-setup"
                    >
                      Setup Agent
                    </button>
                  </div>
                )}
              </div>
              <IconButton
                variant="ghost"
                label="Close help assistant"
                onClick={() => onOpenChange(false)}
                data-testid="button-close-help-assistant"
                className="-mr-1.5 -mt-1.5 h-8 w-8 shrink-0"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </IconButton>
            </header>

            <div ref={scroller} className="flex-1 overflow-auto px-4 py-3 space-y-3">
              {!info && !setupInfo && <Skeleton className="h-16 w-full rounded-2xl" />}
              {turns.map((t, i) => (
                <div key={i} className={t.role === "user" ? "" : "space-y-2"}>
                  <div
                    className={
                      t.role === "user"
                        ? "ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap"
                        : "mr-auto max-w-[90%] rounded-2xl rounded-tl-sm bg-muted px-3 py-2 text-sm whitespace-pre-wrap"
                    }
                    data-testid={`bubble-help-${t.role}-${i}`}
                  >
                    {t.content}
                  </div>
                  {t.role === "assistant" && t.action && (
                    <div className="mr-auto max-w-[92%] rounded-xl border bg-card p-2.5 shadow-sm" data-testid={`action-card-${i}`}>
                      <div className="flex items-start gap-1.5 flex-wrap">
                        <Badge variant="secondary" className="text-[9px] uppercase tracking-wider">
                          <Wand2 className="w-2.5 h-2.5 mr-1" />{t.action.tool.replace(/_/g, " ")}
                        </Badge>
                        <p className="text-[12px] font-medium flex-1 min-w-0">{t.action.summary}</p>
                      </div>
                      {Object.keys(t.action.params).length > 0 && (
                        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                          {Object.entries(t.action.params).slice(0, 6).map(([k, v]) => (
                            <div key={k} className="contents">
                              <dt className="text-muted-foreground">{k}</dt>
                              <dd className="font-mono break-words">
                                {typeof v === "string" ? (v.length > 80 ? v.slice(0, 80) + "…" : v) : JSON.stringify(v)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        {t.actionState === "pending" && (
                          <>
                            <Button size="sm" className="h-7 text-xs px-2" onClick={() => decide(i, true)} data-testid={`button-approve-${i}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => decide(i, false)} data-testid={`button-reject-${i}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                            </Button>
                          </>
                        )}
                        {t.actionState === "running" && <span className="text-[11px] text-muted-foreground">Running…</span>}
                        {t.actionState === "approved" && (
                          <span className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />{t.resultSummary || "Done."}
                          </span>
                        )}
                        {t.actionState === "rejected" && (
                          <span className="text-[11px] text-muted-foreground">Rejected — nothing changed.</span>
                        )}
                        {t.actionState === "error" && (
                          <span className="text-[11px] text-rose-600 dark:text-rose-400">{t.resultSummary}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {pending && (
                <div className="mr-auto max-w-[60%] rounded-2xl rounded-tl-sm bg-muted px-3 py-2 text-sm flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:120ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:240ms]" />
                </div>
              )}

              {/* Suggestion chips, only on the very first turn */}
              {turns.length <= 1 && (mode === "setup" ? setupInfo : info) && (
                <div className="pt-1 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" /> Try asking
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {((mode === "setup" ? setupInfo?.suggestions : info?.suggestions) || []).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        disabled={pending}
                        data-testid={`suggestion-${s.slice(0, 24).replace(/\W+/g, "-")}`}
                        className="text-[11px] px-2.5 py-1 rounded-full border bg-background hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="border-t p-3 flex items-center gap-2 bg-background"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={mode === "setup" ? "Tell me what to set up…" : "Ask how to set up something…"}
                disabled={pending}
                aria-label={mode === "setup" ? "Setup instruction" : "Help question"}
                data-testid="input-help-message"
                className="flex-1"
              />
              <IconButton
                type="submit"
                label="Send message"
                disabled={pending || !input.trim()}
                data-testid="button-send-help-message"
              >
                <Send className="w-4 h-4" aria-hidden="true" />
              </IconButton>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export function HelpAssistantPinnedCard() {
  const { openHelp } = useHelpAssistant();
  const { data: info } = useQuery<HelpInfo>({ queryKey: ["/api/help/info"] });
  const suggestions = useMemo(() => info?.suggestions.slice(0, 3) ?? [], [info]);

  return (
    <Card
      className="border-dashed border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background"
      data-testid="card-help-assistant-pinned"
    >
      <CardContent className="p-5 flex flex-col sm:flex-row gap-4 sm:items-center">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground shrink-0">
          <MessageCircleQuestion className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold" data-testid="text-help-pinned-title">
              {info?.name ?? "Platform Help Assistant"}
            </h3>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
              Always available
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {info?.tagline ??
              "Built-in expert that explains every feature, tailored to your plan and role."}
          </p>
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {suggestions.map((s) => (
                <span
                  key={s}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        <Button onClick={openHelp} data-testid="button-open-help-from-pinned" className="sm:self-center">
          <Sparkles className="w-4 h-4 mr-2" />
          Ask the help bot
        </Button>
      </CardContent>
    </Card>
  );
}
